import React, { useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { ListGroup } from 'react-bootstrap'
import { useSet, useToggle } from 'react-use'
import type { PivotLayout } from '@ztechapps/common.contract'
import type { PivotData } from '../pivot/types'
import type { PivotField } from '@ztechapps/common.contract'
import type { PivotFieldDragPayload } from '../pivot/pivotFieldDrag.utils'
import type { PivotZoneEnum } from '../pivot/PivotZoneEnum'
import { PivotDateGroupEnum, PivotFieldTypeEnum } from '@ztechapps/common.contract'
import { BusyButton } from '@ztechapps/webui.button'
import { CheckboxInput, TextInput } from '@ztechapps/webui.input'
import { HorizontalStack, Icon, ShowIf } from '@ztechapps/webui.util'
import { filterPivotFields } from '../pivot/filterPivotFields'
import { PIVOT_FIELD_DRAG_TYPE, findPivotZoneDragType } from '../pivot/pivotFieldDrag.utils'
import { checkPivotLayoutField, removePivotLayoutItem, uncheckPivotLayoutField } from '../pivot/pivotLayout.utils'
import { toPivotDatePartLabel } from '../pivot/toPivotDatePartLabel'
import styles from './FieldList.module.scss'

/** Props for the All fields list of the pivot fields dialog. */
export type FieldListProps = {
  data: PivotData
  layout: PivotLayout
  onChange: (layout: PivotLayout) => void
}

// Day has no stored grouping, so it is the trailing undefined entry
const DATE_PARTS: (PivotDateGroupEnum | undefined)[] = [
  PivotDateGroupEnum.Year,
  PivotDateGroupEnum.Quarter,
  PivotDateGroupEnum.Month,
  undefined,
]

/** Searchable All fields list — every row ticks into the draft layout or drags into a zone. */
export const FieldList = ({ data, layout, onChange }: FieldListProps) => {
  const [search, setSearch] = useState('')
  const [, { toggle, has }] = useSet<string>()
  // the rows' own drags carry the list format, which the finder never returns — a drag within the list changes nothing
  const [dropActive, setDropActive] = useToggle(false)

  const visibleFields = filterPivotFields(data.fields, search)

  const isChecked = (field: PivotField, dateGroup?: PivotDateGroupEnum): boolean => {
    const inZones = [...layout.rows, ...layout.columns, ...layout.filters].some(
      (item) => item.field === field.name && item.dateGroup === dateGroup
    )
    // a Values aggregation of a date field counts the field, not the part — parts track zones only
    if (field.type === PivotFieldTypeEnum.Date) return inZones
    return inZones || layout.measures.some((item) => item.field === field.name)
  }

  const renderFieldRow = (field: PivotField, dateGroup: PivotDateGroupEnum | undefined, label: string) => (
    <ListGroup.Item
      key={`${field.name}|${dateGroup ?? ''}`}
      className="py-1 px-2 small"
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData(PIVOT_FIELD_DRAG_TYPE, JSON.stringify({ field: field.name, dateGroup }))
        event.dataTransfer.effectAllowed = 'move'
      }}
    >
      <HorizontalStack gap={2}>
        <div className="flex-grow-1">
          <CheckboxInput
            label={label}
            checked={isChecked(field, dateGroup)}
            onChange={(checked: boolean) =>
              onChange(
                checked
                  ? checkPivotLayoutField(layout, field, dateGroup)
                  : uncheckPivotLayoutField(layout, field, dateGroup)
              )
            }
          />
        </div>
        <span aria-hidden="true" className="text-secondary">
          <Icon name="bars" />
        </span>
      </HorizontalStack>
    </ListGroup.Item>
  )

  return (
    <div>
      <div className="small fw-semibold">All fields</div>
      <TextInput placeholder="Search fields" value={search} onChange={(event) => setSearch(event.target.value)} />
      <ListGroup
        className={`overflow-y-auto ${styles.fieldList}${dropActive ? ` ${styles.fieldListDropTarget}` : ''}`}
        // the rows carry no drop hooks of their own, so a zone item's drag is caught here rather than on capture
        onDragOver={(event) => {
          if (findPivotZoneDragType(event.dataTransfer.types)) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
            setDropActive(true)
          }
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropActive(false)
        }}
        onDrop={(event) => {
          const dragType = findPivotZoneDragType(event.dataTransfer.types)
          if (!dragType) return
          event.preventDefault()
          setDropActive(false)
          // a zone format always carries the layout zone and index the item was written with
          const payload = JSON.parse(event.dataTransfer.getData(dragType)) as PivotFieldDragPayload
          onChange(removePivotLayoutItem(layout, payload.zone as PivotZoneEnum, payload.index as number))
        }}
      >
        {visibleFields.map((field) => {
          if (field.type !== PivotFieldTypeEnum.Date) return renderFieldRow(field, undefined, field.name)
          const expanded = has(field.name)
          return (
            <React.Fragment key={field.name}>
              <ListGroup.Item className="py-1 px-2 small">
                <HorizontalStack gap={2}>
                  <BusyButton
                    variant="link"
                    bsSize="sm"
                    className="p-0"
                    icon={expanded ? 'chevron-down' : 'chevron-right'}
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${field.name}`}
                    aria-expanded={expanded}
                    onClick={() => toggle(field.name)}
                  />
                  <div className="flex-grow-1">{field.name}</div>
                </HorizontalStack>
              </ListGroup.Item>
              <ShowIf condition={expanded || !isEmpty(search)}>
                {DATE_PARTS.map((part) => renderFieldRow(field, part, toPivotDatePartLabel(field.name, part)))}
              </ShowIf>
            </React.Fragment>
          )
        })}
      </ListGroup>
    </div>
  )
}
